<template>
  <OnboardingStep
    heading="Confirm Your Role &amp; Plan"
    :back-button="{ name: $routes.Dashboard.name }"
    :commit="commit"
  >
    <template #default>
      <p v-if="!plansLoaded">Loading...</p>
      <template v-else>
        <div class="onboarding-plan" v-if="originalPlan">
          <input
            type="radio"
            name="plan"
            class="onboarding-plan-input"
            :id="getPlanInputId(originalPlan)"
            :value="originalPlan.roleId"
            :aria-labelledby="getPlanNameId(originalPlan)"
            :aria-describedby="getPlanDescriptionId(originalPlan)"
            :checked="selectedPlan === originalPlan"
            @change="selectedPlan = originalPlan"
            ref="planInputs"
          />
          <label class="onboarding-plan-body" :for="getPlanInputId(originalPlan)">
            <div class="onboarding-plan-radio">
              <icon-check-curved />
            </div>
            <div class="onboarding-plan-content">
              <SubscriptionLevelName :level="originalPlan" :id="getPlanNameId(originalPlan)" />
              <p v-if="getPlanDescription(originalPlan)" :id="getPlanDescriptionId(originalPlan)">
                {{ getPlanDescription(originalPlan) }}
              </p>
              <SubscriptionLevelDetails :level="originalPlan" class="button-inline">
                See Full Description
              </SubscriptionLevelDetails>
            </div>
          </label>
        </div>
        <template v-if="originalPlan && otherPlans.length">
          <hr />
          <h3>Other Plans</h3>
        </template>
        <template v-if="otherPlans.length">
          <div v-for="plan of otherPlans" :key="`plan-${plan.roleId}`" class="onboarding-plan">
            <input
              type="radio"
              name="plan"
              class="onboarding-plan-input"
              :id="getPlanInputId(plan)"
              :value="plan.roleId"
              :aria-labelledby="getPlanNameId(plan)"
              :aria-describedby="getPlanDescriptionId(plan)"
              :checked="selectedPlan === plan"
              @change="selectedPlan = plan"
              ref="planInputs"
            />
            <label class="onboarding-plan-body" :for="getPlanInputId(plan)">
              <div class="onboarding-plan-radio">
                <icon-check-curved />
              </div>
              <div class="onboarding-plan-content">
                <SubscriptionLevelName :level="plan" :id="getPlanNameId(plan)" />
                <p v-if="getPlanDescription(plan)" :id="getPlanDescriptionId(plan)">
                  {{ getPlanDescription(plan) }}
                </p>
                <SubscriptionLevelDetails :level="plan" class="button-inline">
                  See Full Description
                </SubscriptionLevelDetails>
              </div>
            </label>
          </div>
        </template>
      </template>
    </template>

    <template #sidebar>
      <div class="card-billing-cycle card-v2">
        <h3>Billing Cycle</h3>
        <label :class="{ selected: billingPeriod === 'monthly' }" class="billing-cycle-option flex">
          <input type="radio" v-model="billingPeriod" value="monthly" style="display: none" />
          Monthly
          <div v-if="selectedPlan && !selectedPlan.isFree" class="align-right">
            <SubscriptionLevelPrice :level="selectedPlan" period="monthly" />
          </div>
        </label>
        <label :class="{ selected: billingPeriod === 'yearly' }" class="billing-cycle-option flex">
          <input type="radio" v-model="billingPeriod" value="yearly" style="display: none" />
          Yearly
          <div v-if="selectedPlan && !selectedPlan.isFree" class="align-right flex">
            <div v-if="selectedPlan.yearlyDiscount > 0" class="save-badge">
              Save {{ formatPercentage(selectedPlan.yearlyDiscount) }}
            </div>
            <SubscriptionLevelPrice :level="selectedPlan" period="yearly" />
          </div>
        </label>
      </div>
    </template>
  </OnboardingStep>
</template>

<script>
import { toString } from '@/helpers/convert';
import onboarding from '@/mixins/onboarding';
import SubscriptionLevel from '@/models/SubscriptionLevel';

export default {
  name: 'OnboardingRole',
  mixins: [onboarding],
  onboardingStep: {
    isApplicable: ({ user }) => user,
  },
  data() {
    return {
      /** @type {boolean} */
      showBenefitsModal: false,
    };
  },
  computed: {
    /**
     * @returns {Intl.NumberFormat['format']}
     */
    formatCurrency() {
      return this.$getCurrencyFormat().format;
    },
    /**
     * @returns {Intl.NumberFormat['format']}
     */
    formatPercentage() {
      return this.$getNumberFormat({ style: 'percent' }).format;
    },
    /**
     * @returns {SubscriptionLevel | null}
     */
    originalPlan() {
      /** @type {Subscription | null} */
      const subscription = this.$store.getters['subscriptions/current'];
      return SubscriptionLevel.from(subscription?.roleId);
    },
    /**
     * @returns {readonly SubscriptionLevel[]}
     */
    otherPlans() {
      const { originalPlan, plans } = this;
      const originalRoleId = this.originalPlan?.roleId;
      if (!originalPlan || originalRoleId == null) {
        return plans;
      }
      return Object.freeze(
        this.plans.filter(
          (plan) =>
            plan.roleId !== originalRoleId &&
            plan.monthlyPricePerMonth >= originalPlan.monthlyPricePerMonth
        )
      );
    },
    /**
     * @returns {readonly SubscriptionLevel[]}
     */
    plans() {
      /** @type {readonly SubscriptionLevel[] | null} */
      let plans = this.$store.getters['subscriptions/levels'];
      if (!plans) {
        plans = [];
      } else {
        plans = plans.filter((plan) => !plan.isFree);
      }
      return Object.freeze(plans);
    },
    /**
     * @returns {boolean}
     */
    plansLoaded() {
      return !!this.$store.getters['subscriptions/levels'];
    },
    selectedPlan: {
      /**
       * @returns {SubscriptionLevel | null}
       */
      get() {
        return this.$store.getters['onboarding/plan'];
      },
      /**
       * @param {SubscriptionLevel | null} value
       * @returns {void}
       */
      set(value) {
        this.$store.commit('onboarding/setPlan', value);
      },
    },
    /**
     * @returns {number}
     */
    selectedPlanIndex() {
      const plan = this.selectedPlan;
      if (!plan) {
        return -1;
      }
      // The user's original plan is always at the top of the list. Therefore...
      if (plan === this.originalPlan) {
        // ...if they haven't selected a different plan, then return 0.
        return 0;
      }
      // ...otherwise, `otherPlans` excludes the original plan, so its index is incremented by 1.
      const index = (this.otherPlans || []).indexOf(plan);
      return index >= 0 ? index + 1 : -1;
    },
  },
  methods: {
    /**
     * @returns {boolean}
     */
    commit() {
      if (this.selectedPlan) {
        return true;
      }
      /** @type {HTMLInputElement} */
      const input = this.$refs.planInputs[0];
      input.setCustomValidity('Please select a plan to continue.');
      input.reportValidity();
      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    },
    /**
     * @param {SubscriptionLevel} plan
     * @returns {string}
     */
    getPlanDescription(plan) {
      return toString(plan.longDescription || plan.description).trim();
    },
    /**
     * @param {SubscriptionLevel} plan
     * @returns {string | null}
     */
    getPlanDescriptionId(plan) {
      return (this.getPlanDescription(plan) && this.getPlanElementId(plan, 'description')) || null;
    },
    /**
     * @param {SubscriptionLevel} plan
     * @param {string | null} [suffix]
     * @returns {string}
     */
    getPlanElementId(plan, suffix) {
      if ((suffix = toString(suffix).trim())) {
        suffix = `-${suffix}`;
      } else {
        suffix = '';
      }
      return `${this.$componentId}-plan-${plan.roleId}${suffix}`;
    },
    /**
     * @param {SubscriptionLevel} plan
     * @returns {string}
     */
    getPlanInputId(plan) {
      return this.getPlanElementId(plan);
    },
    /**
     * @param {SubscriptionLevel} plan
     * @returns {string}
     */
    getPlanNameId(plan) {
      return this.getPlanElementId(plan, 'name');
    },
    /**
     * @returns {void}
     */
    launchBenefitsModal() {
      this.showBenefitsModal = true;
    },
  },
  mounted() {
    const store = this.$store;
    store.dispatch('subscriptions/getLevels');
    store.dispatch('subscriptions/getMySubscription', true);
  },
  watch: {
    selectedPlan() {
      this.$refs.planInputs[0].setCustomValidity('');
    },
    selectedPlanIndex: {
      /**
       * @param {number} index
       * @returns {void}
       */
      handler(index) {
        this.$store.commit('onboarding/setPlanIndex', index);
      },
      immediate: true,
    },
  },
};
</script>
